import { useMemo, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Badge,
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  Spinner,
} from '@klaro7/ui';

import { useBulkSetErrorLogStatus, useErrorLogs, useSetErrorLogStatus } from '@/features/error-log/hooks/useErrorLogs';
import {
  SEVERITY_LABELS,
  SOURCE_FILTERS,
  SOURCE_LABELS,
  describeError,
  relativeTime,
  type ErrorDescription,
  type Severity,
} from '@/features/error-log/lib/describe';
import type { ErrorLogActor, ErrorLogEntry } from '@/features/error-log/services/error-log.service';
import { DeviceIcon } from '@/features/calls/lib/deviceInfo';

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function actorName(actor?: ErrorLogActor | null): string {
  const name = `${actor?.profile?.firstName ?? ''} ${actor?.profile?.lastName ?? ''}`.trim();
  return name || 'admin';
}

const SEVERITY_BADGE: Record<Severity, 'info' | 'warning' | 'error'> = { info: 'info', warning: 'warning', error: 'error' };
const SEVERITY_BAR: Record<Severity, string> = { info: 'border-l-blue-500', warning: 'border-l-amber-500', error: 'border-l-red-500' };

/**
 * Le due spunte di triage della riga. Indipendenti: "visto" = l'ho letto,
 * "gestito" = risolto (ed è quest'ultima a spegnere il pallino in sidebar).
 * Mettere "gestito" segna implicitamente anche "visto", lato server.
 */
function StatusChecks({ entry }: { entry: ErrorLogEntry }) {
  const setStatus = useSetErrorLogStatus();
  const pending = setStatus.isPending && setStatus.variables?.id === entry._id;
  const seen = Boolean(entry.seenAt);
  const handled = Boolean(entry.handledAt);

  const row = (
    label: 'Visto' | 'Gestito',
    checked: boolean,
    at: string | null | undefined,
    by: ErrorLogActor | null | undefined,
    onToggle: () => void
  ) => (
    <label
      className="flex cursor-pointer items-center gap-2 whitespace-nowrap"
      title={checked && at ? `${label} da ${actorName(by)} · ${formatDateTime(at)}` : `Segna come ${label.toLowerCase()}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={pending}
        onChange={onToggle}
        className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal disabled:opacity-50"
      />
      <span className={checked ? 'text-xs font-medium text-emerald-700' : 'text-xs text-neutral-500'}>
        {checked ? label : `Non ${label.toLowerCase()}`}
      </span>
    </label>
  );

  return (
    <div className="space-y-1">
      {row('Visto', seen, entry.seenAt, entry.seenBy, () => setStatus.mutate({ id: entry._id, seen: !seen }))}
      {row('Gestito', handled, entry.handledAt, entry.handledBy, () => setStatus.mutate({ id: entry._id, handled: !handled }))}
    </div>
  );
}

interface Described {
  entry: ErrorLogEntry;
  d: ErrorDescription;
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
        active ? 'bg-brand-teal text-white' : 'border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
      }`}
    >
      {children}
    </button>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3">
      <div className="text-xs text-neutral-600">{label}</div>
      <div className={`text-2xl font-bold ${tone ?? 'text-neutral-900'}`}>{value}</div>
    </div>
  );
}

function FocusCard({ items, onOpen }: { items: Described[]; onOpen: (id: string) => void }) {
  return (
    <div className="overflow-hidden rounded-xl border border-red-200 bg-white">
      <div className="border-b border-red-100 bg-red-50 px-5 py-3">
        <h3 className="text-sm font-semibold text-red-800">Da guardare per primi</h3>
        <p className="text-xs text-red-700">Gli errori non ancora gestiti, dal più recente.</p>
      </div>
      <ul className="divide-y divide-neutral-100">
        {items.map(({ entry, d }) => (
          <li key={entry._id}>
            <button
              type="button"
              onClick={() => onOpen(entry._id)}
              className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-teal"
            >
              <Badge variant="error">{SEVERITY_LABELS.error}</Badge>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-neutral-900">{d.title}</span>
                <span className="block text-xs text-neutral-600">{d.sourceLabel} · {formatDateTime(entry.createdAt)}</span>
              </span>
              <span className="shrink-0 text-xs font-semibold text-brand-teal">Cosa fare →</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">{title}</h4>
      {children}
    </section>
  );
}

const callIdOf = (entry: ErrorLogEntry): string | null => {
  const id = (entry.context ?? {})['callSessionId'];
  return typeof id === 'string' && id ? id : null;
};

function DetailDialog({
  current,
  list,
  all,
  onSelect,
  onClose,
}: {
  current: Described;
  list: Described[];
  all: Described[];
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const { entry, d } = current;
  const callId = callIdOf(entry);
  const siblings = callId ? all.filter((x) => x.entry._id !== entry._id && callIdOf(x.entry) === callId) : [];
  const index = list.findIndex((x) => x.entry._id === entry._id);
  const prev = index > 0 ? list[index - 1] : undefined;
  const next = index >= 0 && index < list.length - 1 ? list[index + 1] : undefined;
  const ctx = (entry.context ?? {}) as Record<string, unknown>;
  const userAgent = typeof ctx.userAgent === 'string' ? ctx.userAgent : null;
  const deviceModel = typeof ctx.deviceModel === 'string' ? ctx.deviceModel : null;
  const showOriginal = entry.message.trim() !== d.title.trim();
  const handled = Boolean(entry.handledAt);

  return (
    <Modal open onOpenChange={(open) => { if (!open) onClose(); }}>
      <ModalContent className="left-auto right-0 top-0 flex h-screen max-h-screen w-full max-w-xl translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-y-0 border-r-0 p-0">
        <ModalHeader className="space-y-1 border-b border-neutral-100 bg-neutral-50 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2 pr-6">
            <Badge variant={SEVERITY_BADGE[d.severity]}>{SEVERITY_LABELS[d.severity]}</Badge>
            <Badge variant="default">{d.sourceLabel}</Badge>
            {entry.code && <span className="font-mono text-xs text-neutral-500">{entry.code}</span>}
          </div>
          <ModalTitle className="text-lg font-semibold text-neutral-900">{d.title}</ModalTitle>
          <ModalDescription className="text-sm text-neutral-500">
            {formatDateTime(entry.createdAt)} · {relativeTime(entry.createdAt)}
          </ModalDescription>
        </ModalHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 text-sm">
          <Section title="Cosa significa">
            <p className="text-neutral-800">{d.meaning}</p>
          </Section>

          <Section title="Cosa fare">
            <p className={`rounded-lg border px-3 py-2 ${handled ? 'border-neutral-200 bg-neutral-50 text-neutral-700' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
              {d.action}
            </p>
          </Section>

          {d.facts.length > 0 && (
            <Section title="Dettagli">
              <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1.5">
                {d.facts.map((fact) => (
                  <div key={`${fact.label}-${fact.value}`} className="contents">
                    <dt className="text-neutral-500">{fact.label}</dt>
                    <dd className={`break-words text-neutral-900 ${fact.mono ? 'font-mono text-xs leading-5' : ''}`}>
                      {fact.href ? (
                        <Link to={fact.href} className="text-brand-teal hover:underline">{fact.value}</Link>
                      ) : (
                        fact.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </Section>
          )}

          {(userAgent || deviceModel) && (
            <Section title="Dispositivo">
              <DeviceIcon userAgent={userAgent} deviceModel={deviceModel} />
            </Section>
          )}

          {siblings.length > 0 && (
            <Section title="Altre voci della stessa chiamata">
              <ul className="space-y-1">
                {siblings.map((x) => (
                  <li key={x.entry._id}>
                    <button
                      type="button"
                      onClick={() => onSelect(x.entry._id)}
                      className="flex w-full items-baseline gap-2 rounded px-1 py-0.5 text-left hover:bg-neutral-50"
                    >
                      <span className="shrink-0 font-mono text-xs text-neutral-500">{formatDateTime(x.entry.createdAt)}</span>
                      <span className="text-neutral-900">{x.d.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {showOriginal && (
            <Section title="Messaggio originale del sistema">
              <p className="rounded-lg bg-neutral-50 px-3 py-2 text-neutral-700">{entry.message}</p>
            </Section>
          )}

          <Section title="Stato">
            <StatusChecks entry={entry} />
            {(entry.seenAt || entry.handledAt) && (
              <p className="mt-2 text-xs text-neutral-500">
                {entry.handledAt
                  ? `Gestito da ${actorName(entry.handledBy)} · ${formatDateTime(entry.handledAt)}`
                  : `Visto da ${actorName(entry.seenBy)} · ${formatDateTime(entry.seenAt!)}`}
              </p>
            )}
          </Section>

          {entry.context && (
            <details>
              <summary className="cursor-pointer text-xs font-medium text-brand-teal hover:underline">Dettagli tecnici</summary>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded bg-neutral-50 px-2 py-1 text-xs text-neutral-600">
                {JSON.stringify(entry.context, null, 2)}
              </pre>
            </details>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-3">
          <button
            type="button"
            onClick={() => prev && onSelect(prev.entry._id)}
            disabled={!prev}
            className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          >
            ← Più recente
          </button>
          <span className="text-xs text-neutral-500">{index >= 0 ? `${index + 1} di ${list.length}` : ''}</span>
          <button
            type="button"
            onClick={() => next && onSelect(next.entry._id)}
            disabled={!next}
            className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          >
            Meno recente →
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
}

export function AdminErrorLogsPage() {
  const [page, setPage] = useState(1);
  const [source, setSource] = useState('');
  const [severity, setSeverity] = useState<'all' | Severity>('all');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Voci spuntate per le azioni di gruppo (diverso da selectedId = voce aperta).
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const bulk = useBulkSetErrorLogStatus();

  const { data, isLoading, isError, isFetching, refetch } = useErrorLogs(page, source || undefined);
  const pagination = data?.pagination;

  const described = useMemo<Described[]>(
    () => (data?.items ?? []).map((entry) => ({ entry, d: describeError(entry) })),
    [data]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return described.filter(({ entry, d }) => {
      if (severity !== 'all' && d.severity !== severity) return false;
      if (onlyOpen && entry.handledAt) return false;
      if (!q) return true;
      const haystack = [d.title, d.meaning, d.sourceLabel, entry.code ?? '', entry.message, ...d.facts.map((f) => f.value)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [described, severity, onlyOpen, query]);

  const counts = useMemo(() => {
    const c: Record<Severity, number> = { info: 0, warning: 0, error: 0 };
    for (const x of described) c[x.d.severity] += 1;
    return c;
  }, [described]);
  const openItems = described.filter((x) => !x.entry.handledAt);
  const openCount = openItems.length;
  const openErrors = openItems.filter((x) => x.d.severity === 'error');
  const openWarnings = openItems.filter((x) => x.d.severity === 'warning');
  const selected = selectedId ? described.find((x) => x.entry._id === selectedId) : undefined;

  // Si agisce solo sulle voci spuntate ancora visibili con i filtri attuali.
  const checkedVisible = filtered.filter((x) => checked.has(x.entry._id)).map((x) => x.entry._id);
  const allChecked = filtered.length > 0 && checkedVisible.length === filtered.length;
  const toggleChecked = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(filtered.map((x) => x.entry._id)));
  const markChecked = (flags: { seen?: boolean; handled?: boolean }) =>
    bulk.mutate({ ids: checkedVisible, ...flags }, { onSuccess: () => setChecked(new Set()) });

  const onRowKey = (e: KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedId(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Log errori</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Cosa è successo nel sistema, spiegato in italiano. Clicca una voce per il dettaglio e cosa fare. Conservati 90 giorni.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
        >
          {isFetching ? 'Aggiorno…' : 'Aggiorna'}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Da gestire" value={openCount} />
        <SummaryCard label="Errori da gestire" value={openErrors.length} tone="text-red-700" />
        <SummaryCard label="Attenzione da gestire" value={openWarnings.length} tone="text-amber-700" />
        <SummaryCard label="Informazioni" value={counts.info} tone="text-blue-700" />
      </div>

      {openErrors.length > 0 && <FocusCard items={openErrors.slice(0, 3)} onOpen={setSelectedId} />}

      <div className="space-y-3 rounded-xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-medium text-neutral-500">Gravità</span>
          <FilterChip active={severity === 'all'} onClick={() => setSeverity('all')}>Tutte ({described.length})</FilterChip>
          <FilterChip active={severity === 'error'} onClick={() => setSeverity('error')}>Errori ({counts.error})</FilterChip>
          <FilterChip active={severity === 'warning'} onClick={() => setSeverity('warning')}>Attenzione ({counts.warning})</FilterChip>
          <FilterChip active={severity === 'info'} onClick={() => setSeverity('info')}>Informazioni ({counts.info})</FilterChip>
          <span className="mx-1 h-5 w-px bg-neutral-200" />
          <FilterChip active={onlyOpen} onClick={() => setOnlyOpen((v) => !v)}>Solo da gestire ({openCount})</FilterChip>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-16 text-xs font-medium text-neutral-500">Fonte</span>
          <FilterChip active={source === ''} onClick={() => { setSource(''); setPage(1); }}>Tutte</FilterChip>
          {SOURCE_FILTERS.map((s) => (
            <FilterChip key={s} active={source === s} onClick={() => { setSource(s); setPage(1); }}>
              {SOURCE_LABELS[s] ?? s}
            </FilterChip>
          ))}
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca nel testo, nel codice o in un numero"
          aria-label="Cerca nei log"
          className="h-9 w-full rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal sm:w-96"
        />
        <p className="text-xs text-neutral-500">I conteggi e la ricerca valgono per le voci di questa pagina (50 alla volta).</p>
      </div>

      {isError && (
        <Alert variant="error">
          <AlertDescription>Errore nel caricamento dei log. Riprova più tardi.</AlertDescription>
        </Alert>
      )}

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
            />
            {allChecked ? 'Deseleziona tutti' : 'Seleziona tutti'}
          </label>
          <span className="text-xs text-neutral-500">
            {checkedVisible.length === 0 ? 'Nessuna voce selezionata' : checkedVisible.length === 1 ? '1 voce selezionata' : `${checkedVisible.length} voci selezionate`}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => markChecked({ seen: true })}
              disabled={checkedVisible.length === 0 || bulk.isPending}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
            >
              Segna come visti
            </button>
            <button
              type="button"
              onClick={() => markChecked({ handled: true })}
              disabled={checkedVisible.length === 0 || bulk.isPending}
              className="rounded-lg bg-brand-teal px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
            >
              {bulk.isPending ? 'Salvo…' : 'Segna come gestiti'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center rounded-xl border border-neutral-200 bg-white py-16"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white py-16 text-center text-sm text-neutral-500">
            {described.length === 0 ? 'Nessun errore registrato.' : 'Nessuna voce con questi filtri.'}
          </div>
        ) : (
          filtered.map(({ entry, d }) => (
            <div
              key={entry._id}
              className={`flex items-stretch gap-3 rounded-xl border border-l-4 border-neutral-200 bg-white ${SEVERITY_BAR[d.severity]} ${entry.handledAt ? 'opacity-75' : ''}`}
            >
              <label className="flex shrink-0 cursor-pointer items-center pl-3">
                <input
                  type="checkbox"
                  checked={checked.has(entry._id)}
                  onChange={() => toggleChecked(entry._id)}
                  aria-label={`Seleziona: ${d.title}`}
                  className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                />
              </label>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(entry._id)}
                onKeyDown={(e) => onRowKey(e, entry._id)}
                className="min-w-0 flex-1 cursor-pointer rounded-l-lg px-4 py-3 hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-teal"
                aria-label={`Apri i dettagli: ${d.title}`}
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant={SEVERITY_BADGE[d.severity]}>{SEVERITY_LABELS[d.severity]}</Badge>
                  <Badge variant="default">{d.sourceLabel}</Badge>
                  <span className="text-neutral-500" title={formatDateTime(entry.createdAt)}>
                    {relativeTime(entry.createdAt)} · {formatDateTime(entry.createdAt)}
                  </span>
                  {entry.code && <span className="font-mono text-neutral-400">{entry.code}</span>}
                </div>
                <h3 className="mt-1 text-sm font-semibold text-neutral-900">{d.title}</h3>
                <p className="mt-0.5 line-clamp-2 text-sm text-neutral-600">{d.meaning}</p>
                <span className="mt-1 inline-block text-xs font-medium text-brand-teal">Vedi dettagli e cosa fare →</span>
              </div>
              <div className="shrink-0 self-center border-l border-neutral-100 px-4 py-3">
                <StatusChecks entry={entry} />
              </div>
            </div>
          ))
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500">
            {pagination.total} voci · pagina {pagination.page} di {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.hasPrevPage}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.hasNextPage}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Successiva
            </button>
          </div>
        </div>
      )}

      {selected && (
        <DetailDialog
          current={selected}
          list={filtered}
          all={described}
          onSelect={setSelectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
